/**
 * Servizi ICT esposti dalle tab dell'app.
 *
 * `tab` è il valore del parametro `?tab=` usato nel contentUrl del manifest:
 * ogni tab statica punta a questa pagina con il proprio parametro. Senza
 * parametro la pagina mostra tutti i servizi.
 */

export interface IctSite {
  /** Identificativo usato in `?tab=` e come entityId nel manifest. */
  id: 'assistenza' | 'pam' | 'network-agent';
  name: string;
  description: string;
  url: string;
  /** Avvertenza mostrata sotto la descrizione. */
  note: string | null;
}

export const ICT_SITES: IctSite[] = [
  {
    id: 'assistenza',
    name: 'Assistenza ICT',
    description: 'Apertura e gestione delle richieste di assistenza (ServiceDesk Plus).',
    url: 'https://assistenza.enea.it/',
    note: null,
  },
  {
    id: 'pam',
    name: 'PAM',
    description: 'Accesso privilegiato ai sistemi (PAM360).',
    url: 'https://pam.enea.it/',
    note: 'Raggiungibile solo dalla rete ENEA o in VPN.',
  },
  {
    id: 'network-agent',
    name: 'Network Agent',
    description: 'Console di gestione dell\'agente di rete (staging).',
    url: 'https://staging-ict.enea.it/network-agent/',
    note: 'Accesso con l\'account ENEA (Microsoft Entra ID): si apre nel browser, dove la sessione è già attiva.',
  },
];

/** true se la stringa è un URL http/https assoluto. */
export function isValidSiteUrl(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Servizi da mostrare: quello richiesto dal parametro `tab`, oppure tutti.
 * Un parametro sconosciuto (o un URL manomesso) non deve mai rompere la pagina.
 */
export function resolveSites(tab: string | null | undefined, sites: IctSite[] = ICT_SITES): IctSite[] {
  const wanted = (tab ?? '').trim().toLowerCase();
  if (!wanted) return sites;
  const match = sites.find((s) => s.id === wanted);
  return match ? [match] : sites;
}

/** Etichetta del pulsante di apertura. */
export function openLabel(site: IctSite): string {
  return `Apri ${site.name}`;
}

/** Sottotitolo della pagina in base a cosa sta mostrando. */
export function pageSubtitle(visible: IctSite[]): string {
  if (visible.length === 1) return visible[0]?.description ?? '';
  return 'Assistenza e accesso privilegiato';
}
